'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonBurger,
  ButtonHome,
  ButtonPromo,
  ButtonSearch,
} from '@/components/buttons';
import { Support } from '@/components/Support';
import { generateLink } from '@/helpers/common';
import { useBodyBottomPadding } from '@/hooks/body';
import useEventListener from '@/hooks/eventListener';
import { SearchModalTabBar } from '@/modules/lobby/components/FilterGames/Search';
import { useHasUnreadBonus } from '@/modules/promotions/bonusNotification';
import { useGetCommonStore } from '@/store';
import { ModalNav } from '../Header/HeaderMobile/ModalNav';
import type { ServerLink } from '../helpers';

import { ScTabBar } from './styled';
import { TabBarItem } from './TabBarItem';

enum ModalType {
  Menu = 'Menu',
  Search = 'Search',
}

type TabBarProps = {
  title: string;
  locale: string;
  links: ServerLink[];
};

export const TabBar = ({ title, locale, links }: TabBarProps) => {
  const useCommonStore = useGetCommonStore();

  const hasUnreadBonus = useHasUnreadBonus();
  const [modal, setModal] = useState<ModalType>();

  const ref = useRef<HTMLDivElement>(null);

  const pathname = usePathname();

  const { setBodyBottomPadding } = useBodyBottomPadding();

  useEffect(() => {
    const handleScroll = () => {
      if (ref == null || ref.current == null) return;
      setBodyBottomPadding(ref.current.offsetHeight);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setBodyBottomPadding]);

  const handleChangeModal = useCallback(
    (modalType: ModalType | undefined, cb?: () => void) => () => {
      setModal((modal) => (modalType === modal ? undefined : modalType));
      cb && cb();
    },
    []
  );

  const isActive = useCallback(
    (modalType: ModalType) => modal === modalType,
    [modal]
  );

  const controller = (modalType: ModalType, cb?: () => void) => ({
    onClick: handleChangeModal(modalType, cb),
    active: isActive(modalType),
  });

  const modalComponent = useMemo(() => {
    switch (modal) {
      case ModalType.Menu:
        return (
          <ModalNav
            links={links}
            locale={locale}
            onClose={handleChangeModal(ModalType.Menu)}
            tabBarTitle={title}
          />
        );
      case ModalType.Search:
        return (
          <SearchModalTabBar
            onClose={handleChangeModal(ModalType.Search)}
            padding="16px 0 0 0"
          />
        );
      default:
        return null;
    }
  }, [locale, modal, title, links, handleChangeModal]);

  const handleTabBarHeight = useCommonStore.use.handleTabBarHeight();

  useEventListener('resize', () => handleTabBarHeight(ref), ref, true);

  const handleHomeClick = () => {
    handleChangeModal(undefined)();
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  useEffect(() => {
    setModal(undefined);
  }, [pathname]);

  return (
    <>
      {modalComponent}
      <ScTabBar data-testid="mobile-tabbar" isModal={!!modal} ref={ref}>
        <div className="body">
          <TabBarItem>
            <Link
              className={pathname === generateLink('/', locale) ? 'active' : ''}
              data-testid="mobile-home-button"
              href={generateLink('/', locale)}
              onClick={handleHomeClick}
            >
              <ButtonHome />
            </Link>
          </TabBarItem>
          <TabBarItem>
            <div data-testid="mobile-support-button">
              <Support />
            </div>
          </TabBarItem>
          <TabBarItem>
            <div data-testid="mobile-search-button">
              <ButtonSearch {...controller(ModalType.Search)} />
            </div>
          </TabBarItem>
          <TabBarItem badge={hasUnreadBonus && <span className="promo-dot" />}>
            <Link
              className={
                pathname === generateLink('/promotions', locale) ? 'active' : ''
              }
              data-testid="mobile-promotions-button"
              href={generateLink('/promotions', locale)}
              onClick={handleChangeModal(undefined)}
            >
              <ButtonPromo />
            </Link>
          </TabBarItem>
          <TabBarItem>
            <div data-testid="mobile-menu-button">
              <ButtonBurger {...controller(ModalType.Menu)} />
            </div>
          </TabBarItem>
        </div>
      </ScTabBar>
    </>
  );
};
